import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  ShieldAlert,
  Monitor,
  Users,
  Globe,
  FileWarning,
  Mail,
  AppWindow,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Activity,
  Sparkles,
  RefreshCw,
  Brain,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getDashboardOverview, explainRisk, getDashboardAIInsights } from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import OnboardingChecklist from '../components/OnboardingChecklist';

const threatClassColors = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  safe: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  malicious: 'bg-red-500/10 text-red-500 border-red-500/20',
  suspicious: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
};

const RiskGauge = ({ score }) => {
  const getColor = () => {
    if (score >= 75) return '#ef4444';
    if (score >= 50) return '#f97316';
    if (score >= 25) return '#f59e0b';
    return '#10b981';
  };

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#1e293b"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke={getColor()}
          strokeWidth="8"
          strokeDasharray={`${score * 2.51} 251`}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-heading font-bold text-white">{score}</span>
        <span className="text-xs text-slate-500 uppercase">Risk Score</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, change, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-500',
    red: 'bg-red-500/10 text-red-500',
    green: 'bg-emerald-500/10 text-emerald-500',
    amber: 'bg-amber-500/10 text-amber-500',
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-6 h-6" />
          </div>
          {change && (
            <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-red-500' : 'text-emerald-500'}`}>
              <TrendingUp className={`w-3 h-3 ${trend === 'down' ? 'rotate-180' : ''}`} />
              {change}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-2xl font-heading font-bold text-white">{value}</p>
          <p className="text-sm text-slate-500 mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const ThreatItem = ({ type, data }) => {
  const icons = {
    dns: Globe,
    files: FileWarning,
    emails: Mail,
    apps: AppWindow,
  };
  const Icon = icons[type] || AlertTriangle;

  const getTitle = () => {
    if (type === 'dns') return data.domain;
    if (type === 'files') return data.file_name;
    if (type === 'emails') return data.subject;
    if (type === 'apps') return data.process_name;
    return 'Unknown';
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
        data.classification === 'critical' || data.classification === 'malicious' 
          ? 'bg-red-500/10' 
          : data.classification === 'high' || data.classification === 'suspicious'
          ? 'bg-orange-500/10'
          : 'bg-slate-800'
      }`}>
        <Icon className={`w-4 h-4 ${
          data.classification === 'critical' || data.classification === 'malicious'
            ? 'text-red-500'
            : data.classification === 'high' || data.classification === 'suspicious'
            ? 'text-orange-500'
            : 'text-slate-400'
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{getTitle()}</p>
        <p className="text-xs text-slate-500">
          {new Date(data.timestamp).toLocaleString()}
        </p>
      </div>
      <Badge className={`${threatClassColors[data.classification]} border`}>
        {data.classification}
      </Badge>
    </motion.div>
  );
};

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    fetchAIInsights();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getDashboardOverview();
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const response = await getDashboardAIInsights();
      setAiInsight(response.data);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Activity className="w-8 h-8 text-blue-500 animate-pulse" />
      </div>
    );
  }

  const allThreats = [
    ...(overview?.recent_threats?.dns || []).map(t => ({ ...t, type: 'dns' })),
    ...(overview?.recent_threats?.files || []).map(t => ({ ...t, type: 'files' })),
    ...(overview?.recent_threats?.emails || []).map(t => ({ ...t, type: 'emails' })),
    ...(overview?.recent_threats?.apps || []).map(t => ({ ...t, type: 'apps' })),
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 8);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Onboarding Checklist */}
      <OnboardingChecklist />
      
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">Threat Overview</h1>
        <p className="text-slate-400 mt-1">Real-time security monitoring across all protection layers</p>
      </div>

      {/* Risk Score & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Risk Score Card */}
        <Card className="lg:col-span-1 bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Organization Risk</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <RiskGauge score={overview?.risk_score || 0} />
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Monitor}
            label="Protected Devices"
            value={overview?.stats?.devices_protected || 0}
            color="blue"
          />
          <StatCard
            icon={Users}
            label="Protected Users"
            value={overview?.stats?.users_protected || 0}
            color="blue"
          />
          <StatCard
            icon={ShieldAlert}
            label="Threats Blocked"
            value={overview?.stats?.total_threats_blocked || 0}
            color="red"
          />
          <StatCard
            icon={CheckCircle}
            label="Files Quarantined"
            value={overview?.stats?.files_quarantined || 0}
            color="amber"
          />
        </div>
      </div>

      {/* AI Insights Card */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/20">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                AI Security Insight
                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                  GPT-5.1
                </Badge>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                {aiInsight?.generated_at ? `Generated ${new Date(aiInsight.generated_at).toLocaleTimeString()}` : 'Powered by SentinelAI'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchAIInsights}
            disabled={loadingAI}
            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
          >
            {loadingAI ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1" />
                Refresh
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          {loadingAI ? (
            <div className="flex items-center gap-3 py-4">
              <RefreshCw className="w-5 h-5 text-purple-500 animate-spin" />
              <span className="text-slate-400">Analyzing your security posture...</span>
            </div>
          ) : aiInsight?.insight ? (
            <div className="space-y-4">
              <p className="text-slate-300 leading-relaxed">{aiInsight.insight}</p>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                  {aiInsight.data?.total_blocked || 0} threats blocked
                </Badge>
                <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                  {aiInsight.data?.critical_dns || 0} critical DNS
                </Badge>
                <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                  {aiInsight.data?.critical_files || 0} critical files
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-slate-400">Unable to generate AI insight. Click refresh to try again.</p>
          )}
        </CardContent>
      </Card>

      {/* Threat Trend Chart */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Threat Activity (Last 7 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overview?.threat_trend || []}>
                <defs>
                  <linearGradient id="colorDns" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorFiles" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorEmails" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0f172a', 
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    color: '#f8fafc'
                  }}
                />
                <Area type="monotone" dataKey="dns" stroke="#3b82f6" fill="url(#colorDns)" name="DNS" />
                <Area type="monotone" dataKey="files" stroke="#f97316" fill="url(#colorFiles)" name="Files" />
                <Area type="monotone" dataKey="emails" stroke="#ef4444" fill="url(#colorEmails)" name="Emails" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Protection Layer Stats & Recent Threats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Protection Layers */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white">Protection Layers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-blue-500" />
                <span className="text-sm text-slate-300">DNS Firewall</span>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-3">
                <FileWarning className="w-5 h-5 text-orange-500" />
                <span className="text-sm text-slate-300">File Sandbox</span>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-500" />
                <span className="text-sm text-slate-300">Email Shield</span>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
              <div className="flex items-center gap-3">
                <AppWindow className="w-5 h-5 text-purple-500" />
                <span className="text-sm text-slate-300">App Guard</span>
              </div>
              <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Active</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Recent Threats */}
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white">Recent Threats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {allThreats.length > 0 ? (
                allThreats.map((threat, index) => (
                  <ThreatItem key={threat.id || index} type={threat.type} data={threat} />
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p>No recent threats detected</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
